import React, { useState, useEffect, useMemo } from 'react';
import { applicationService } from '../../services/applicationService';

const APPLICATION_STATUSES = {
  new: { label: 'Mới', tone: 'new' },
  viewed: { label: 'Đã xem', tone: 'reviewing' },
  interview: { label: 'Mời phỏng vấn', tone: 'shortlisted' },
  rejected: { label: 'Từ chối', tone: 'rejected' }
};

function sourceTone(source) {
  if (!source) return 'others';
  if (source.startsWith('welcome')) return 'welcome';
  if (source.startsWith('tuyen-dung')) return 'tuyendung';
  return 'others';
}

function formatDate(iso, withSeconds) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const opts = withSeconds
      ? { hour: '2-digit', minute: '2-digit', second: '2-digit' }
      : { hour: '2-digit', minute: '2-digit' };
    return `${date} · ${d.toLocaleTimeString('vi-VN', opts)}`;
  } catch (e) {
    return iso;
  }
}

export default function ApplicationsManager() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', source: '', position: '', search: '' });
  const [selectedApp, setSelectedApp] = useState(null);

  const loadApplications = () => {
    setLoading(true);
    applicationService
      .getAll()
      .then((res) => {
        if (res.data) setApplications(res.data);
      })
      .catch((err) => alert(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadApplications();
  }, []);

  const stats = useMemo(() => {
    const counts = { new: 0, viewed: 0, interview: 0, rejected: 0 };
    applications.forEach((a) => { if (counts[a.status] != null) counts[a.status]++; });
    return counts;
  }, [applications]);

  const sourceOptions = useMemo(() => {
    const seen = new Map();
    applications.forEach((a) => { if (a.source && !seen.has(a.source)) seen.set(a.source, true); });
    return Array.from(seen.keys()).sort((a, b) => a.localeCompare(b, 'vi'));
  }, [applications]);

  const positionOptions = useMemo(() => {
    const seen = new Map();
    applications.forEach((a) => {
      const label = (a.position_label || '').trim();
      if (label && !seen.has(label)) seen.set(label, true);
    });
    return Array.from(seen.keys()).sort((a, b) => a.localeCompare(b, 'vi'));
  }, [applications]);

  const filtered = applications.filter((a) => {
    if (filters.status && a.status !== filters.status) return false;
    if (filters.source && a.source !== filters.source) return false;
    if (filters.position && (a.position_label || '') !== filters.position) return false;
    if (filters.search) {
      const hay = [a.full_name, a.email, a.phone, a.position_label, a.note]
        .map((v) => (v || '').toString().toLowerCase()).join(' ');
      if (!hay.includes(filters.search.toLowerCase())) return false;
    }
    return true;
  });

  const handleClearFilters = () => setFilters({ status: '', source: '', position: '', search: '' });

  const handleUpdateStatus = async (id, status) => {
    const prev = applications;
    setApplications((list) => list.map((a) => (a.id === id ? { ...a, status } : a)));
    try {
      await applicationService.updateStatus(id, status);
      if (selectedApp && selectedApp.id === id) {
        setSelectedApp((s) => ({ ...s, status }));
      }
    } catch (err) {
      alert('Lỗi cập nhật: ' + err.message);
      setApplications(prev);
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Xoá hồ sơ của "${name}"?\n\nFile CV đính kèm cũng sẽ bị xoá.\nHành động không thể hoàn tác.`)) {
      try {
        await applicationService.delete(id);
        setSelectedApp(null);
        loadApplications();
      } catch (err) {
        alert(err.message);
      }
    }
  };

  const handleDownload = async (app) => {
    if (!app.cv_path) {
      alert('Hồ sơ này không có CV');
      return;
    }
    try {
      await applicationService.downloadCv(app.id, app.cv_filename);
    } catch (err) {
      alert('Lỗi tải CV: ' + err.message);
    }
  };

  return (
    <section className="admin-panel">
      <div className="panel-head">
        <div>
          <h2>Hồ sơ ứng tuyển</h2>
          <p className="panel-head__sub">Hồ sơ gửi qua form Welcome + Tuyển dụng. Click "Xem" để mở chi tiết + tải CV.</p>
        </div>
        <button className="btn btn--ghost" type="button" onClick={loadApplications} style={{ padding: '10px 18px', fontSize: '.85rem' }}>
          ↻ Tải lại
        </button>
      </div>

      {applications.length > 0 && (
        <div className="apps-stats">
          <span className="apps-stat"><strong>{applications.length}</strong> tổng</span>
          <span className="apps-stat apps-stat--new"><strong>{stats.new}</strong> mới</span>
          <span className="apps-stat apps-stat--reviewing"><strong>{stats.viewed}</strong> đã xem</span>
          <span className="apps-stat apps-stat--shortlisted"><strong>{stats.interview}</strong> mời phỏng vấn</span>
          <span className="apps-stat apps-stat--rejected"><strong>{stats.rejected}</strong> từ chối</span>
        </div>
      )}

      <div className="apps-filters">
        <label className="apps-filter">
          <span>Trạng thái</span>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">Tất cả</option>
            {Object.entries(APPLICATION_STATUSES).map(([key, info]) => (
              <option key={key} value={key}>{info.label}</option>
            ))}
          </select>
        </label>
        <label className="apps-filter">
          <span>Nguồn</span>
          <select value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}>
            <option value="">Tất cả</option>
            {sourceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="apps-filter">
          <span>Vị trí</span>
          <select value={filters.position} onChange={(e) => setFilters({ ...filters, position: e.target.value })}>
            <option value="">Tất cả</option>
            {positionOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="apps-filter apps-filter--grow">
          <span>Tìm tên / email / SĐT</span>
          <input
            type="search"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="vd: Nguyễn Văn A, 0989…, gmail.com"
          />
        </label>
      </div>

      {loading ? (
        <p className="muted">Đang tải…</p>
      ) : applications.length === 0 ? (
        <p className="muted">Chưa có hồ sơ ứng tuyển nào. Khi ứng viên submit form, hồ sơ sẽ xuất hiện ở đây.</p>
      ) : filtered.length === 0 ? (
        <p className="muted">
          Không có hồ sơ khớp bộ lọc. <button type="button" className="link-btn" onClick={handleClearFilters}>Xoá bộ lọc</button>
        </p>
      ) : (
        <div className="jobs-list">
          {filtered.map((a) => {
            const statusInfo = APPLICATION_STATUSES[a.status] || APPLICATION_STATUSES.new;
            const shortId = (a.id || '').slice(0, 8).toUpperCase();
            return (
              <div key={a.id} className="app-row" data-status={a.status || 'new'}>
                <div className="app-row__main">
                  <div className="app-row__head">
                    <span className="app-row__name">{a.full_name || '—'}</span>
                    <span className="app-row__code" title="Mã hồ sơ">#{shortId}</span>
                    <span className={`app-row__source app-row__source--${sourceTone(a.source)}`}>{a.source || '—'}</span>
                  </div>
                  <div className="app-row__contact">
                    <a href={`mailto:${a.email || ''}`}>{a.email || ''}</a> · <a href={`tel:${a.phone || ''}`}>{a.phone || ''}</a>
                  </div>
                  <div className="app-row__meta">
                    <strong>Vị trí:</strong> {a.position_label || 'Vị trí khác / không chọn'}
                    {' '}· <span className="app-row__date">{formatDate(a.created_at)}</span>
                    {a.note && <span className="app-row__note-flag" title="Có ghi chú"> 📝</span>}
                  </div>
                </div>
                <div className="app-row__actions">
                  <select
                    className={`app-status-select app-status-select--${statusInfo.tone}`}
                    aria-label="Trạng thái hồ sơ"
                    value={a.status || 'new'}
                    onChange={(e) => handleUpdateStatus(a.id, e.target.value)}
                  >
                    {Object.entries(APPLICATION_STATUSES).map(([key, info]) => (
                      <option key={key} value={key}>{info.label}</option>
                    ))}
                  </select>
                  <button className="icon-btn" type="button" title="Xem chi tiết + tải CV" onClick={() => setSelectedApp(a)}>👁</button>
                  <button className="icon-btn" type="button" title="Tải CV" disabled={!a.cv_path} onClick={() => handleDownload(a)}>⬇</button>
                  <button className="icon-btn icon-btn--danger" type="button" title="Xoá hồ sơ" onClick={() => handleDelete(a.id, a.full_name)}>🗑</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedApp && (
        <div className="admin-modal">
          <div className="admin-modal__backdrop" onClick={() => setSelectedApp(null)} />
          <div className="admin-modal__panel admin-modal__panel--narrow" role="dialog" aria-modal="true">
            <button className="admin-modal__close" aria-label="Đóng" type="button" onClick={() => setSelectedApp(null)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
            <div className="admin-modal__body">
              <h2>Hồ sơ ứng tuyển</h2>
              <div className="app-detail__code">Mã hồ sơ: <code>#{(selectedApp.id || '').slice(0, 8).toUpperCase()}</code></div>
              <dl className="app-detail">
                <dt>Họ tên</dt><dd>{selectedApp.full_name || '—'}</dd>
                <dt>Email</dt><dd><a href={`mailto:${selectedApp.email || ''}`}>{selectedApp.email || ''}</a></dd>
                <dt>SĐT</dt><dd><a href={`tel:${selectedApp.phone || ''}`}>{selectedApp.phone || ''}</a></dd>
                <dt>Vị trí ứng tuyển</dt>
                <dd>
                  {selectedApp.position_label || 'Vị trí khác / không chọn'}
                  {selectedApp.position_key && <code> ({selectedApp.position_key})</code>}
                </dd>
                <dt>Nguồn submit</dt><dd>{selectedApp.source || '—'}</dd>
                <dt>Trạng thái hiện tại</dt><dd>{(APPLICATION_STATUSES[selectedApp.status] || APPLICATION_STATUSES.new).label}</dd>
                <dt>Thời điểm submit</dt><dd>{formatDate(selectedApp.created_at, true)}</dd>
                <dt>File CV</dt>
                <dd>{selectedApp.cv_filename ? selectedApp.cv_filename : <em>Không có</em>}</dd>
                {selectedApp.note && (
                  <>
                    <dt>Ghi chú</dt>
                    <dd className="app-detail__note">{selectedApp.note}</dd>
                  </>
                )}
              </dl>
              <div className="form-actions" style={{ justifyContent: 'space-between' }}>
                <button
                  type="button"
                  className="btn btn--ghost icon-btn--danger"
                  style={{ border: '1px solid #f5c2bc', color: '#c0392b' }}
                  onClick={() => handleDelete(selectedApp.id, selectedApp.full_name)}
                >
                  🗑 Xoá hồ sơ
                </button>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" className="btn btn--ghost" onClick={() => setSelectedApp(null)}>Đóng</button>
                  <button type="button" className="btn btn--primary" disabled={!selectedApp.cv_path} onClick={() => handleDownload(selectedApp)}>
                    Tải CV
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
